import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import PhygitalIcon from "@/components/PhygitalIcon";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  PartyPopper,
  Building2,
  Calendar,
  Users,
  Zap,
  Sparkles,
  CreditCard,
  Globe
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Event types
const EVENT_TYPES = [
  { id: "mixer", name: "Mixer / Networking", desc: "Social events, happy hours" },
  { id: "conference", name: "Conference", desc: "Multi-day, sessions, speakers" },
  { id: "popup", name: "Pop-up / Market", desc: "Retail, vendors, booths" },
  { id: "private", name: "Private Event", desc: "Invite-only gatherings" },
  { id: "other", name: "Other", desc: "Something unique" }
];

const HostSetupPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [orgName, setOrgName] = useState("");
  const [orgDescription, setOrgDescription] = useState("");
  const [eventTypes, setEventTypes] = useState([]);
  const [website, setWebsite] = useState("");
  const [wantsNFC, setWantsNFC] = useState(false);

  const toggleEventType = (typeId) => {
    setEventTypes(prev => 
      prev.includes(typeId) ? prev.filter(t => t !== typeId) : [...prev, typeId]
    );
  };

  const handleSubmit = async () => {
    if (!orgName.trim()) {
      toast.error("Please enter your organization name");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/organizations`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: orgName,
          description: orgDescription,
          event_types: eventTypes,
          website,
          wants_nfc: wantsNFC
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success("Organization created!");
        navigate(`/host/events/new?org=${data.org_id}`);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to create organization");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <Link to="/" className="flex items-center gap-2">
            <PhygitalIcon className="w-8 h-8" />
          </Link>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2].map((s) => (
            <div 
              key={s}
              className={`w-3 h-3 rounded-full transition-all ${
                s === step ? 'w-8 bg-orange-500' : s < step ? 'bg-orange-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Organization Info */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mx-auto mb-4">
                <PartyPopper className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Create Your Organization</h1>
              <p className="text-gray-600">Set up your event hosting profile</p>
            </div>

            {/* FREE Badge */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <Badge className="bg-green-500 text-white mb-2">FREE FOREVER</Badge>
              <p className="text-green-800 font-medium">Event hosting is completely free</p>
              <p className="text-green-600 text-sm">Unlimited events, unlimited guests, full analytics</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Host Name *</label>
                <Input
                  placeholder="e.g., Ocean AI, HTHF Events"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <Textarea
                  placeholder="What does your organization do?"
                  value={orgDescription}
                  onChange={(e) => setOrgDescription(e.target.value)}
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    placeholder="https://..."
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What types of events do you host?</label>
                <div className="grid grid-cols-2 gap-2">
                  {EVENT_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => toggleEventType(type.id)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        eventTypes.includes(type.id)
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <p className="font-medium text-sm">{type.name}</p>
                      <p className="text-xs text-gray-600">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button 
              onClick={() => setStep(2)} 
              disabled={!orgName.trim()}
              className="w-full bg-orange-500 hover:bg-orange-600"
              size="lg"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Features & Complete */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">What you get</h1>
              <p className="text-gray-600">Everything included for free</p>
            </div>

            {/* Features */}
            <Card className="border-orange-200">
              <CardContent className="p-6 space-y-4">
                {[
                  { icon: Calendar, title: "Unlimited Events", desc: "Create as many events as you need" },
                  { icon: Users, title: "Unlimited Guests", desc: "No cap on RSVPs or attendees" },
                  { icon: Sparkles, title: "Preference Collection", desc: "Dietary, accessibility, interests" },
                  { icon: CreditCard, title: "Digital Passes", desc: "QR codes for every guest" },
                  { icon: Building2, title: "Host Dashboard", desc: "Full analytics and guest management" }
                ].map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-4 h-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{feature.title}</p>
                      <p className="text-sm text-gray-600">{feature.desc}</p>
                    </div>
                    <Check className="w-5 h-5 text-green-500 ml-auto flex-shrink-0" />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* NFC Add-on */}
            <Card className={`border-2 transition-all cursor-pointer ${wantsNFC ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}
              onClick={() => setWantsNFC(!wantsNFC)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900">NFC Card Reader</p>
                      <Badge variant="secondary">Legacy Benefit</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">Tap-to-check-in at your events</p>
                    <p className="text-lg font-bold text-green-600">Included Free</p>
                    <p className="text-xs text-gray-500">Free for life with legacy access</p>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${wantsNFC ? 'border-purple-500 bg-purple-500' : 'border-gray-300'}`}>
                    {wantsNFC && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                {loading ? "Creating..." : "Create Organization"}
                <PartyPopper className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className="text-center text-sm text-gray-600">
              You'll be able to create your first event right after
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default HostSetupPage;
